// ============================================================
//  WebsiteWidgetPage.js — Website Widget (standalone live chat, like tawk.to)
//
//  Chats   — answer website visitors in real time
//  Widgets — look, behaviour, allowed websites and the code to install
//
//  You're "Online" while CommandKit is open and set to Online; otherwise
//  visitors see your away message, leave their details, and you get an email.
// ============================================================
import './WebsiteWidgetPage.css';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  getWidgets, createWidget, updateWidget, rotateWidgetKey, deleteWidget, widgetScriptUrl, liveChatPresence,
  uploadWidgetLogo, removeWidgetLogo, widgetFileUrl,
} from '../../api/client';
import LiveChatInbox from './LiveChatInbox';
import PushBell from '../../components/layout/PushBell';
import AiAgentPanel from './AiAgentPanel';
import BrandingCard from './BrandingCard';
import { lcSoundOn, setLcSound, playChime } from '../../components/layout/useLiveChatPulse';

const COLORS = ['#00C88C', '#2563EB', '#7C3AED', '#F59E0B', '#E11D48', '#0D1A14'];
const MAX_BUTTONS = 6;
const LAUNCHERS = [
  ['chat', 'fa-solid fa-comment', 'Chat'],
  ['headset', 'fa-solid fa-headset', 'Support'],
  ['help', 'fa-solid fa-circle-question', 'Help'],
  ['message', 'fa-regular fa-message', 'Message'],
  ['smile', 'fa-regular fa-face-smile', 'Smile'],
  ['logo', null, 'Your logo'],
];

function pick(w) {
  return {
    label: w.label, title: w.title, color: w.color, greeting: w.greeting, awayMessage: w.awayMessage, position: w.position,
    buttons: (w.buttons || []).map(b => (typeof b === 'string' ? b : b.label)),
    askDetails: !!w.askDetails, showWhatsApp: !!w.showWhatsApp, whatsapp: w.whatsapp || '',
    sound: !!w.sound, emailAlerts: !!w.emailAlerts, domains: [...(w.domains || [])], enabled: !!w.enabled,
    agentName: w.agentName || '', showAgentName: w.showAgentName !== false,
    launcherIcon: w.launcherIcon || 'chat', allowUploads: w.allowUploads !== false,
  };
}

function onColor(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return '#07150E';
  const n = parseInt(m[1], 16);
  const lum = c => { c /= 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; };
  const L = 0.2126 * lum((n >> 16) & 255) + 0.7152 * lum((n >> 8) & 255) + 0.0722 * lum(n & 255);
  return L > 0.36 ? '#07150E' : '#FFFFFF';
}

function timeAgo(s) {
  if (!s) return null;
  const t = new Date(String(s).replace(' ', 'T')).getTime();
  if (Number.isNaN(t)) return null;
  const m = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  return `${Math.round(h / 24)} d ago`;
}

function cleanDomain(v) {
  return String(v || '').trim().toLowerCase().replace(/^[a-z]+:\/\//, '').replace(/[/?#].*$/, '').replace(/\.$/, '');
}

function seenFor(seen, d) {
  if (!seen) return null;
  if (d.startsWith('*.')) {
    const base = d.slice(2);
    const hits = Object.keys(seen).filter(h => h === base || h.endsWith('.' + base)).map(h => seen[h]).sort();
    return hits.length ? hits[hits.length - 1] : null;
  }
  return seen[d] || seen['www.' + d] || null;
}

function Switch({ on, onChange, label, disabled }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled}
      className={`wwg-switch ${on ? 'is-on' : ''}`} onClick={() => onChange(!on)}>
      <span />
    </button>
  );
}

// ── live preview (static mock that follows the form) ──────────
function LauncherIcon({ icon, logo }) {
  if (icon === 'logo' && logo) return <img src={widgetFileUrl(logo)} alt="" className="wwg-lg" />;
  const f = LAUNCHERS.find(l => l[0] === icon && l[1]) || LAUNCHERS[0];
  return <i className={f[1]} />;
}

function Preview({ form, online, logo }) {
  const on = onColor(form.color);
  const title = form.title || 'Chat with us';
  const replyAs = form.showAgentName ? (form.agentName.trim() || form.title.trim() || 'Support') : '';
  const initials = title.trim().split(/\s+/).slice(0, 2).map(p => p[0]?.toUpperCase()).join('') || 'C';
  const chips = form.buttons.filter(b => b.trim());
  return (
    <div className={`wwg-pv ${form.position === 'left' ? 'is-left' : ''}`} aria-hidden="true">
      <div className="wwg-pv-panel">
        <div className="wwg-pv-head" style={{ background: form.color, color: on }}>
          <span className="wwg-pv-av" style={on === '#FFFFFF' ? { background: 'rgba(255,255,255,.22)', color: '#fff' } : { color: form.color }}>
            {logo ? <img src={widgetFileUrl(logo)} alt="" /> : initials}<i className={online ? 'is-on' : ''} style={{ borderColor: form.color }} />
          </span>
          <span className="wwg-pv-who"><b>{title}</b><span>{online ? 'Online · we reply in a few minutes' : 'Away · leave a message'}</span></span>
          {form.sound && <i className="fa-regular fa-bell" />}
          <i className="fa-solid fa-chevron-down" />
        </div>
        <div className="wwg-pv-body">
          {!online && <div className="wwg-pv-away"><i className="fa-regular fa-moon" /> {form.awayMessage || 'We’re away right now.'}</div>}
          {form.greeting && <div className="wwg-pv-bubble">{form.greeting}</div>}
          {chips.length > 0 && (
            <div className="wwg-pv-chips">
              {chips.map((b, i) => <span key={i} style={{ borderColor: form.color, color: on === '#FFFFFF' ? form.color : '#00845D' }}>{b}</span>)}
            </div>
          )}
          <div className="wwg-pv-agent">
            {(replyAs || logo) && <small>{logo && <img src={widgetFileUrl(logo)} alt="" />}{replyAs}</small>}
            <span>Hi! How can I help? 🙂</span>
          </div>
        </div>
        <div className="wwg-pv-foot">{form.allowUploads && <b><i className="fa-regular fa-image" /></b>}<span>Type a message…</span><em style={{ background: form.color, color: on }}><i className="fa-solid fa-arrow-right" /></em></div>
      </div>
      <span className="wwg-pv-launch" style={{ background: form.color, color: on }}><LauncherIcon icon={form.launcherIcon} logo={logo} /></span>
    </div>
  );
}

export default function WebsiteWidgetPage() {
  const [data, setData]         = useState(null);   // { widgets }
  const [presence, setPresence] = useState(null);   // { status, online }
  const [tab, setTab]           = useState('chats');
  const [selId, setSelId]       = useState(null);
  const [form, setForm]         = useState(null);
  const [formFor, setFormFor]   = useState(null);
  const [saving, setSaving]     = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName]   = useState('');
  const [domainIn, setDomainIn] = useState('');
  const [ask, setAsk]           = useState(null);   // 'delete' | 'rotate'
  const [testKey, setTestKey]   = useState(null);
  const [busyAct, setBusyAct]   = useState(false);
  const [sound, setSound]       = useState(lcSoundOn);

  const load = useCallback(async () => {
    try {
      const res = await getWidgets();
      const list = res.data.widgets || [];
      setData({ widgets: list, brandingPrice: res.data.brandingPrice || 0 });
      setPresence(res.data.presence || null);
      setSelId(cur => (cur && list.some(w => w.wId === cur) ? cur : list[0]?.wId ?? null));
      if (!list.length) setTab('widgets');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not load your widgets');
      setData({ widgets: [] });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const selected = useMemo(() => data?.widgets.find(w => w.wId === selId) || null, [data, selId]);
  // Fill the form when a different widget is picked — not on every refresh, so unsaved edits survive.
  useEffect(() => {
    if (!selected) { setForm(null); setFormFor(null); return; }
    if (formFor !== selected.wId) { setForm(pick(selected)); setFormFor(selected.wId); setAsk(null); setDomainIn(''); }
  }, [selected, formFor]);

  const dirty = !!(form && selected && JSON.stringify(form) !== JSON.stringify(pick(selected)));

  // "Try it on this page" — loads the real widget here; removed when leaving.
  useEffect(() => {
    if (!testKey) return undefined;
    window.CommandKitWidget?.destroy?.();
    const s = document.createElement('script');
    s.src = widgetScriptUrl();
    s.async = true;
    s.setAttribute('data-widget', testKey);
    document.body.appendChild(s);
    const t = setTimeout(() => window.CommandKitWidget?.open?.(), 1200);
    return () => { clearTimeout(t); window.CommandKitWidget?.destroy?.(); s.remove(); };
  }, [testKey]);

  async function setStatus(status) {
    try {
      const res = await liveChatPresence(status);
      setPresence(res.data.presence);
      toast.success(status === 'online' ? 'You’re online — visitors see you’re around' : 'You’re away — visitors can leave a message');
    } catch (e) { toast.error(e?.response?.data?.message || 'Could not change your status'); }
  }

  function toggleSound() {
    const next = !sound;
    setSound(next); setLcSound(next);
    if (next) playChime();
  }

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  function replaceWidget(w) {
    setData(d => ({ ...d, widgets: d.widgets.map(x => (x.wId === w.wId ? { ...x, ...w } : x)) }));
  }

  async function save() {
    if (!form || saving) return;
    setSaving(true);
    try {
      const res = await updateWidget(selId, { ...form, buttons: form.buttons.map(b => b.trim()).filter(Boolean) });
      replaceWidget(res.data.widget);
      setForm(pick(res.data.widget));
      toast.success('Saved — your website shows the changes on the next page load');
      if (testKey) setTestKey(null);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not save');
    }
    setSaving(false);
  }

  async function toggleEnabled(on) {
    try {
      const res = await updateWidget(selId, { enabled: on });
      replaceWidget(res.data.widget);
      setForm(f => (f ? { ...f, enabled: on } : f));
      toast.success(on ? 'Widget is live' : 'Widget switched off — visitors see it as unavailable');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not update');
    }
  }

  async function create(e) {
    e.preventDefault();
    if (busyAct) return;
    setBusyAct(true);
    try {
      const res = await createWidget({ label: newName.trim() || 'Main website' });
      setData(d => ({ ...d, widgets: [res.data.widget, ...d.widgets] }));
      setSelId(res.data.widget.wId);
      setTab('widgets');
      setCreating(false); setNewName('');
      toast.success('Widget created — copy the code onto your website');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not create the widget');
    }
    setBusyAct(false);
  }

  async function rotate() {
    if (ask !== 'rotate') { setAsk('rotate'); return; }
    setBusyAct(true);
    try {
      const res = await rotateWidgetKey(selId);
      replaceWidget(res.data.widget);
      toast.success('New code created — replace the old code on your website');
    } catch (e) { toast.error(e?.response?.data?.message || 'Could not create a new code'); }
    setAsk(null); setBusyAct(false);
  }

  async function remove() {
    if (ask !== 'delete') { setAsk('delete'); return; }
    setBusyAct(true);
    try {
      await deleteWidget(selId);
      if (testKey) setTestKey(null);
      setData(d => ({ ...d, widgets: d.widgets.filter(w => w.wId !== selId) }));
      setSelId(null);
      toast.success('Widget deleted');
      load();
    } catch (e) { toast.error(e?.response?.data?.message || 'Could not delete'); }
    setAsk(null); setBusyAct(false);
  }

  function addDomain(e) {
    e.preventDefault();
    const d = cleanDomain(domainIn);
    if (!d) return;
    if (!/^(\*\.)?[a-z0-9-]+(\.[a-z0-9-]+)*(:\d{1,5})?$/.test(d)) { toast.error('That doesn’t look like a website address'); return; }
    if (form.domains.includes(d)) { setDomainIn(''); return; }
    if (form.domains.length >= 20) { toast.error('Up to 20 websites per widget'); return; }
    set('domains', [...form.domains, d]);
    setDomainIn('');
  }

  const snippet = selected ? `<script src="${widgetScriptUrl()}" data-widget="${selected.key}" async></script>` : '';
  async function copy() {
    try { await navigator.clipboard.writeText(snippet); toast.success('Code copied'); }
    catch {
      const ta = document.createElement('textarea'); ta.value = snippet; document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); toast.success('Code copied'); } catch { toast.error('Copy failed — select the code and copy it'); }
      ta.remove();
    }
  }

  async function onLogo(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/^image\/(png|jpeg|webp|gif)$/.test(file.type)) { toast.error('Use a PNG, JPG, WebP or GIF image'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('Logo must be under 2 MB'); return; }
    setBusyAct(true);
    try {
      const res = await uploadWidgetLogo(selId, file);
      replaceWidget(res.data.widget);
      toast.success('Logo updated');
    } catch (err) { toast.error(err?.response?.data?.message || 'Could not upload the logo'); }
    setBusyAct(false);
  }
  async function dropLogo() {
    setBusyAct(true);
    try {
      const res = await removeWidgetLogo(selId);
      replaceWidget(res.data.widget);
      if (form.launcherIcon === 'logo') set('launcherIcon', 'chat');
      toast.success('Logo removed');
    } catch (err) { toast.error(err?.response?.data?.message || 'Could not remove the logo'); }
    setBusyAct(false);
  }

  function setButton(i, v) { set('buttons', form.buttons.map((b, j) => (j === i ? v : b))); }

  // ── render ────────────────────────────────────────────────
  if (!data) return <div className="wwg"><div className="wwg-loading"><span className="wwg-spin" /> Loading…</div></div>;
  const widgets = data.widgets;
  const away = presence?.status === 'away';

  const createModal = creating && (
    <div className="wwg-modal" role="presentation" onMouseDown={e => { if (e.target === e.currentTarget) setCreating(false); }}>
      <form className="wwg-modal-card" role="dialog" aria-modal="true" aria-labelledby="wwg-new-title" onSubmit={create}>
        <div className="wwg-modal-head">
          <h2 id="wwg-new-title">New widget</h2>
          <button type="button" className="wwg-icon-btn" aria-label="Close" onClick={() => setCreating(false)}><i className="fa-solid fa-xmark" /></button>
        </div>
        <label className="wwg-field">
          <span>Name (only you see this)</span>
          <input autoFocus value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Main website" maxLength={60} />
        </label>
        <p className="wwg-hint">Next you’ll pick the colour, greeting and websites, then copy one line of code onto your site.</p>
        <button type="submit" className="wwg-btn wwg-btn-brand wwg-btn-block" disabled={busyAct}>
          {busyAct ? <span className="wwg-spin" /> : <i className="fa-solid fa-check" />} Create widget
        </button>
      </form>
    </div>
  );

  return (
    <div className={`wwg ${tab === 'chats' && widgets.length ? 'is-chats' : ''}`}>
      <div className="wwg-head">
        <div>
          <h1>Website Widget</h1>
          <p>Live chat for your website — visitors write, you answer here.</p>
        </div>
        <div className="wwg-head-actions">
          {widgets.length > 0 && (
            <div className="wwg-presence" role="radiogroup" aria-label="Your status">
              <button type="button" role="radio" aria-checked={!away} className={!away ? 'is-on' : ''} onClick={() => away && setStatus('online')}>
                <i className="dot is-green" /> Online
              </button>
              <button type="button" role="radio" aria-checked={away} className={away ? 'is-on' : ''} onClick={() => !away && setStatus('away')}>
                <i className="dot" /> Away
              </button>
            </div>
          )}
          {widgets.length > 0 && (
            <button type="button" className={`wwg-icon-btn wwg-sound ${sound ? 'is-on' : ''}`} onClick={toggleSound}
              aria-pressed={sound} aria-label={sound ? 'Mute new message sound' : 'Turn on new message sound'} title={sound ? 'Sound on for new messages' : 'Sound off'}>
              <i className={`fa-solid ${sound ? 'fa-bell' : 'fa-bell-slash'}`} />
            </button>
          )}
          {widgets.length > 0 && <PushBell />}
          {widgets.length > 0 && (
            <a className="wwg-btn wwg-btn-ghost" href="/chats" target="_blank" rel="noopener noreferrer" title="Open the chat-only app — install it to your desktop or phone">
              <i className="fa-solid fa-desktop" /> Chat app
            </a>
          )}
          <button type="button" className="wwg-btn wwg-btn-dark" onClick={() => setCreating(true)}>
            <i className="fa-solid fa-plus" /> New widget
          </button>
        </div>
      </div>

      {widgets.length > 0 && (
        <div className="wwg-tabs" role="tablist" aria-label="Website Widget">
          <button type="button" role="tab" aria-selected={tab === 'chats'} className={tab === 'chats' ? 'is-on' : ''} onClick={() => setTab('chats')}>
            <i className="fa-regular fa-comments" /> Chats
          </button>
          <button type="button" role="tab" aria-selected={tab === 'widgets'} className={tab === 'widgets' ? 'is-on' : ''} onClick={() => setTab('widgets')}>
            <i className="fa-solid fa-sliders" /> Widgets &amp; install
          </button>
          <button type="button" role="tab" aria-selected={tab === 'ai'} className={tab === 'ai' ? 'is-on' : ''} onClick={() => setTab('ai')}>
            <i className="fa-solid fa-wand-magic-sparkles" /> AI agent
          </button>
        </div>
      )}

      {!widgets.length && (
        <div className="wwg-empty">
          <span className="wwg-empty-ic"><i className="fa-solid fa-comment-dots" /></span>
          <h2>Chat with visitors on your website</h2>
          <p>Create a widget, paste one line of code on your site, and answer visitors right here — with a sound when they write and an email if you miss one.</p>
          <button type="button" className="wwg-btn wwg-btn-brand" onClick={() => setCreating(true)}>
            <i className="fa-solid fa-plus" /> Create your first widget
          </button>
        </div>
      )}

      {widgets.length > 0 && tab === 'chats' && <LiveChatInbox widgets={widgets} presence={presence} />}

      {widgets.length > 0 && tab === 'ai' && <AiAgentPanel widgets={widgets} onWidgetUpdated={replaceWidget} />}

      {widgets.length > 0 && tab === 'widgets' && (
        <>
          <div className="wwg-list" role="tablist" aria-label="Your widgets">
            {widgets.map(w => {
              const anySeen = Object.keys(w.seen || {}).length > 0;
              const st = !w.enabled ? ['Off', 'is-off'] : anySeen ? ['Live', 'is-live'] : ['Not installed yet', 'is-wait'];
              return (
                <button key={w.wId} type="button" role="tab" aria-selected={w.wId === selId}
                  className={`wwg-wcard ${w.wId === selId ? 'is-active' : ''}`} onClick={() => setSelId(w.wId)}>
                  <span className="wwg-wcard-top">
                    <span className="wwg-wcard-ic" style={{ background: w.color, color: onColor(w.color) }}><i className="fa-solid fa-comment" /></span>
                    <span className={`wwg-tag ${st[1]}`}>{st[0]}</span>
                  </span>
                  <b>{w.label}</b>
                  <span className="wwg-wcard-sub">{(w.domains || []).slice(0, 2).join(', ') || 'Any website'}</span>
                  <span className="wwg-wcard-stats">
                    <span><i className="fa-regular fa-comments" /> {w.stats?.chats7 ?? 0} chats · 7d</span>
                    <span><i className="fa-regular fa-address-card" /> {w.stats?.leads ?? 0} contacts</span>
                  </span>
                </button>
              );
            })}
          </div>

          {form && selected && (
            <div className="wwg-grid">
              {/* ── left: settings ── */}
              <div className="wwg-col">
                <section className="wwg-card">
                  <div className="wwg-card-row">
                    <div>
                      <h2>{selected.label}</h2>
                      <p className="wwg-sub">{selected.enabled ? 'Live — visitors can chat' : 'Switched off — visitors see “unavailable”'}</p>
                    </div>
                    <Switch on={selected.enabled} onChange={toggleEnabled} label="Widget on" />
                  </div>
                  <label className="wwg-field">
                    <span>Name (only you see this)</span>
                    <input value={form.label} onChange={e => set('label', e.target.value)} maxLength={60} />
                  </label>
                </section>

                <section className="wwg-card">
                  <h3>Logo &amp; bubble icon</h3>
                  <div className="wwg-logo-row">
                    <span className="wwg-logo" style={{ background: selected.logo ? '#fff' : form.color, color: onColor(form.color) }}>
                      {selected.logo ? <img src={widgetFileUrl(selected.logo)} alt="Widget logo" /> : <i className="fa-regular fa-image" />}
                    </span>
                    <div className="wwg-logo-txt">
                      <b>Logo</b>
                      <span>Shown at the top of the chat and next to your replies. Square works best, max 2 MB.</span>
                      <div className="wwg-logo-btns">
                        <label className="wwg-btn wwg-btn-ghost wwg-btn-sm">
                          <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={onLogo} disabled={busyAct} hidden />
                          <i className="fa-solid fa-upload" /> {selected.logo ? 'Change' : 'Upload logo'}
                        </label>
                        {selected.logo && <button type="button" className="wwg-btn wwg-btn-ghost wwg-btn-sm" onClick={dropLogo} disabled={busyAct}>Remove</button>}
                      </div>
                    </div>
                  </div>
                  <div className="wwg-field">
                    <span id="wwg-icon-l">Bubble icon</span>
                    <div className="wwg-icons" role="radiogroup" aria-labelledby="wwg-icon-l">
                      {LAUNCHERS.map(([v, cls, label]) => {
                        const off = v === 'logo' && !selected.logo;
                        return (
                          <button key={v} type="button" role="radio" aria-checked={form.launcherIcon === v} disabled={off}
                            title={off ? 'Upload a logo first' : label}
                            className={`wwg-icon ${form.launcherIcon === v ? 'is-on' : ''}`} onClick={() => set('launcherIcon', v)}>
                            <span style={{ background: form.color, color: onColor(form.color) }}>
                              {v === 'logo' ? (selected.logo ? <img src={widgetFileUrl(selected.logo)} alt="" /> : <i className="fa-regular fa-image" />) : <i className={cls} />}
                            </span>
                            <em>{label}</em>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </section>

                <section className="wwg-card">
                  <h3>Look &amp; messages</h3>
                  <div className="wwg-two">
                    <label className="wwg-field">
                      <span>Name shown in the chat</span>
                      <input value={form.title} onChange={e => set('title', e.target.value)} maxLength={40} placeholder="e.g. PrimeBiller Support" />
                    </label>
                    <div className="wwg-field">
                      <span id="wwg-color-l">Colour</span>
                      <div className="wwg-colors" role="radiogroup" aria-labelledby="wwg-color-l">
                        {COLORS.map(c => (
                          <button key={c} type="button" role="radio" aria-checked={form.color.toUpperCase() === c} aria-label={c}
                            className={`wwg-color ${form.color.toUpperCase() === c ? 'is-on' : ''}`} style={{ background: c }} onClick={() => set('color', c)} />
                        ))}
                        <label className="wwg-color-custom" title="Custom colour">
                          <input type="color" value={form.color} onChange={e => set('color', e.target.value.toUpperCase())} aria-label="Custom colour" />
                          <i className="fa-solid fa-eye-dropper" />
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="wwg-replyas">
                    <label className="wwg-field">
                      <span>Your replies appear as</span>
                      <input value={form.agentName} onChange={e => set('agentName', e.target.value)} maxLength={40}
                        placeholder={form.title.trim() || 'Support'} disabled={!form.showAgentName} />
                      <em>{form.showAgentName ? 'Visitors see this name above your messages — e.g. “WaPay Support” or “Ada”. Empty uses the chat name.' : 'No name is shown above your replies.'}</em>
                    </label>
                    <div className="wwg-replyas-sw">
                      <Switch on={form.showAgentName} onChange={v => set('showAgentName', v)} label="Show a name on replies" />
                      <span>Show name</span>
                    </div>
                  </div>
                  <label className="wwg-field">
                    <span>Greeting</span>
                    <textarea rows={2} value={form.greeting} onChange={e => set('greeting', e.target.value)} maxLength={300} placeholder="Hi there 👋 How can we help you today?" />
                  </label>
                  <label className="wwg-field">
                    <span>Away message</span>
                    <textarea rows={2} value={form.awayMessage} onChange={e => set('awayMessage', e.target.value)} maxLength={300} placeholder="We’re away right now. Leave a message and we’ll get back to you soon." />
                    <em>Shown when you’re set to Away or CommandKit isn’t open.</em>
                  </label>
                  <div className="wwg-field">
                    <span>Bubble position</span>
                    <div className="wwg-seg">
                      {[['left', 'Bottom left'], ['right', 'Bottom right']].map(([v, l]) => (
                        <button key={v} type="button" className={form.position === v ? 'is-on' : ''} aria-pressed={form.position === v} onClick={() => set('position', v)}>{l}</button>
                      ))}
                    </div>
                  </div>
                  <div className="wwg-field">
                    <span>Suggested questions</span>
                    <div className="wwg-qb">
                      {form.buttons.map((b, i) => (
                        <div key={i} className="wwg-qb-row">
                          <input value={b} onChange={e => setButton(i, e.target.value)} placeholder="e.g. How much is 1GB?" maxLength={40} aria-label={`Suggested question ${i + 1}`} />
                          <button type="button" className="wwg-icon-btn" aria-label={`Remove suggested question ${i + 1}`} onClick={() => set('buttons', form.buttons.filter((_, j) => j !== i))}><i className="fa-solid fa-trash-can" /></button>
                        </div>
                      ))}
                      {form.buttons.length < MAX_BUTTONS && (
                        <button type="button" className="wwg-add" onClick={() => set('buttons', [...form.buttons, ''])}>
                          <i className="fa-solid fa-plus" /> Add a suggested question
                        </button>
                      )}
                    </div>
                    <em>Shown under the greeting. A tap sends that question to you, so visitors start faster.</em>
                  </div>
                </section>

                <BrandingCard widget={selected} price={data.brandingPrice}
                  onUpdated={w => w && setData(d => ({ ...d, widgets: d.widgets.map(x => (x.wId === w.wId ? { ...x, ...w } : x)) }))} />

                <section className="wwg-card wwg-toggles">
                  <div className="wwg-toggle">
                    <span className="wwg-toggle-ic"><i className="fa-regular fa-address-card" /></span>
                    <div><b>Ask for name and contact first</b><span>Visitors give their name and phone or email before chatting. When you’re away we always ask, so you can reply later.</span></div>
                    <Switch on={form.askDetails} onChange={v => set('askDetails', v)} label="Ask for name and contact first" />
                  </div>
                  <div className="wwg-toggle">
                    <span className="wwg-toggle-ic"><i className="fa-regular fa-bell" /></span>
                    <div><b>Sound for visitors</b><span>Visitors hear a soft sound when you reply. They can still mute it from the chat.</span></div>
                    <Switch on={form.sound} onChange={v => set('sound', v)} label="Sound for visitors" />
                  </div>
                  <div className="wwg-toggle">
                    <span className="wwg-toggle-ic"><i className="fa-regular fa-image" /></span>
                    <div><b>Photos &amp; screenshots</b><span>Visitors can send a photo or paste a screenshot (max 5 MB). Every image is checked, cleaned of hidden data and kept private.</span></div>
                    <Switch on={form.allowUploads} onChange={v => set('allowUploads', v)} label="Allow photos and screenshots" />
                  </div>
                  <div className="wwg-toggle">
                    <span className="wwg-toggle-ic"><i className="fa-regular fa-envelope" /></span>
                    <div><b>Email me missed chats</b><span>Only when a visitor’s message goes unseen and unanswered for 5 minutes — never for chats you or the AI already handled.</span></div>
                    <Switch on={form.emailAlerts} onChange={v => set('emailAlerts', v)} label="Email me missed chats" />
                  </div>
                  <div className="wwg-toggle">
                    <span className="wwg-toggle-ic"><i className="fa-brands fa-whatsapp" /></span>
                    <div><b>“Continue on WhatsApp” button</b><span>Lets visitors move to your WhatsApp number instead.</span></div>
                    <Switch on={form.showWhatsApp} onChange={v => set('showWhatsApp', v)} label="Continue on WhatsApp button" />
                  </div>
                  {form.showWhatsApp && (
                    <label className="wwg-field wwg-wa">
                      <span>WhatsApp number</span>
                      <input inputMode="tel" value={form.whatsapp} onChange={e => set('whatsapp', e.target.value)} placeholder="2348030000000" />
                    </label>
                  )}
                </section>
              </div>

              {/* ── right: install, preview, websites ── */}
              <div className="wwg-col">
                <section className="wwg-code">
                  <div className="wwg-card-row">
                    <h3>Add to your website</h3>
                    <button type="button" className="wwg-btn wwg-btn-brand wwg-btn-sm" onClick={copy}><i className="fa-regular fa-copy" /> Copy</button>
                  </div>
                  <pre tabIndex={0} aria-label="Widget code">{snippet}</pre>
                  <p>Paste it just before <code>&lt;/body&gt;</code>, or in your WordPress / Wix “custom code” box.</p>
                  <button type="button" className="wwg-btn wwg-btn-ghost wwg-btn-sm" onClick={() => setTestKey(k => (k ? null : selected.key))} disabled={dirty}>
                    <i className={`fa-solid ${testKey ? 'fa-stop' : 'fa-play'}`} /> {testKey ? 'Stop test' : dirty ? 'Save to test it here' : 'Try it on this page'}
                  </button>
                </section>

                <section className="wwg-card wwg-card-pv">
                  <h3>Preview</h3>
                  <Preview form={form} online={!away} logo={selected.logo} />
                </section>

                <section className="wwg-card">
                  <h3>Allowed websites</h3>
                  <p className="wwg-sub">The widget only loads on these addresses. <code>example.com</code> also covers <code>www.example.com</code>; <code>*.example.com</code> covers every subdomain.</p>
                  {form.domains.length === 0 && (
                    <div className="wwg-warn"><i className="fa-solid fa-triangle-exclamation" /> No websites yet — any site with this code can show your chat. Add yours to lock it down.</div>
                  )}
                  <ul className="wwg-domains">
                    {form.domains.map(d => {
                      const s = seenFor(selected.seen, d);
                      return (
                        <li key={d}>
                          <span>{d}</span>
                          <em className={s ? 'is-seen' : ''}>{s ? `Seen ${timeAgo(s)}` : 'Not seen yet'}</em>
                          <button type="button" className="wwg-icon-btn" aria-label={`Remove ${d}`} onClick={() => set('domains', form.domains.filter(x => x !== d))}><i className="fa-solid fa-xmark" /></button>
                        </li>
                      );
                    })}
                  </ul>
                  <form className="wwg-domain-add" onSubmit={addDomain}>
                    <input value={domainIn} onChange={e => setDomainIn(e.target.value)} placeholder="shop.example.com" aria-label="Add a website" />
                    <button type="submit" className="wwg-btn wwg-btn-dark">Add</button>
                  </form>
                </section>

                <section className="wwg-card wwg-danger">
                  <h3>Code &amp; removal</h3>
                  <div className="wwg-danger-row">
                    <div><b>Make a new code</b><span>The current code stops working at once — use it if your code was copied onto a site you don’t own.</span></div>
                    <button type="button" className="wwg-btn wwg-btn-ghost wwg-btn-sm" onClick={rotate} onBlur={() => setAsk(a => (a === 'rotate' ? null : a))} disabled={busyAct}>
                      {ask === 'rotate' ? 'Tap again to confirm' : 'New code'}
                    </button>
                  </div>
                  <div className="wwg-danger-row">
                    <div><b>Delete widget</b><span>Removes the chat from your website and deletes its conversations.</span></div>
                    <button type="button" className="wwg-btn wwg-btn-danger wwg-btn-sm" onClick={remove} onBlur={() => setAsk(a => (a === 'delete' ? null : a))} disabled={busyAct}>
                      {ask === 'delete' ? 'Tap again to delete' : 'Delete'}
                    </button>
                  </div>
                </section>
              </div>
            </div>
          )}

          {dirty && (
            <div className="wwg-savebar" role="region" aria-label="Unsaved changes">
              <span><i className="fa-solid fa-circle-info" /> Unsaved changes</span>
              <div>
                <button type="button" className="wwg-btn wwg-btn-ghost wwg-btn-sm" onClick={() => setForm(pick(selected))} disabled={saving}>Discard</button>
                <button type="button" className="wwg-btn wwg-btn-brand wwg-btn-sm" onClick={save} disabled={saving}>
                  {saving ? <span className="wwg-spin" /> : <i className="fa-solid fa-check" />} Save changes
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {createModal}
    </div>
  );
}
